import React, { useEffect, useState } from "react";
import styles from "./tool-activity-section.module.scss";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import caretRight from "@src/assets/svg/caret-right.svg";
import caretDown from "@src/assets/svg/caret-down.svg";
import checkCircleGreen from "@src/assets/svg/check-circle-green.svg";
import warningTriangleIcon from "@src/assets/svg/warning-triangle.svg";
import { StreamTool } from "./stream-tools";
import { useI18N } from "@src/language/language-context";
import SpinnerWithVariant from "@src/components/atoms/SpinnerWithVariant/spinner-with-variant";

const cssClassName = "tool-activity-section";

interface ToolActivitySectionProps {
  tools: StreamTool[];
  isStreaming: boolean;
}

export const ToolActivitySection: React.FC<ToolActivitySectionProps> = ({
  tools,
  isStreaming,
}) => {
  const i18n = useI18N();
  const texts = {
    toolActivityLabel: i18n("modules.aiRagChat.toolActivityLabel"),
  };
  const [isExpanded, setIsExpanded] = useState(false);
  const doneCount = tools.filter((tool) => tool.status !== "running").length;

  // collapse once this message's stream finishes (e.g. on the "done" event)
  useEffect(() => {
    if (!isStreaming) setIsExpanded(false);
  }, [isStreaming]);

  const onToggle = () => setIsExpanded((prev) => !prev);

  return (
    <div className={styles[`${cssClassName}__wrapper`]}>
      <div
        className={styles[`${cssClassName}__header`]}
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <FlexRowContainer horizontalGap={4}>
          <span className={styles[`${cssClassName}__header-text`]}>
            {texts.toolActivityLabel} ({doneCount}/{tools.length})
          </span>
          <span className={styles[`${cssClassName}__caret`]}>
            {isExpanded ? (
              <img
                className={styles[`${cssClassName}__caret`]}
                src={caretDown}
                alt="Caret Down"
              />
            ) : (
              <img
                className={styles[`${cssClassName}__caret`]}
                src={caretRight}
                alt="Caret Right"
              />
            )}
          </span>
        </FlexRowContainer>
      </div>
      {isExpanded && (
        <div className={styles[`${cssClassName}__content`]}>
          {tools.map((tool) => (
            <div key={tool.key} className={styles[`${cssClassName}__row`]}>
              <span className={styles[`${cssClassName}__status`]}>
                {tool.status === "running" && (
                  <span className={styles[`${cssClassName}__spinner-wrapper`]}>
                    <SpinnerWithVariant variant="extra-small" />
                  </span>
                )}
                {tool.status === "complete" && (
                  <img src={checkCircleGreen} alt="Complete" />
                )}
                {tool.status === "error" && (
                  <img src={warningTriangleIcon} alt="Error" />
                )}
              </span>
              <span className={styles[`${cssClassName}__name`]}>
                {tool.name}
              </span>
              {tool.detail && (
                <span className={styles[`${cssClassName}__detail`]}>
                  {tool.detail}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
